/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable, OnDestroy } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { BehaviorSubject, Observable, Subject, Subscription, lastValueFrom } from 'rxjs';
import { distinctUntilChanged, filter, map, mapTo, skip, take, takeUntil, tap } from 'rxjs/operators';
import { DefaultSplitDataRenderType } from '@touchplan/common';
import { DynamicFlagTypes } from '@touchplan/feature-flags';
import { Attachment } from '../../app-data/models/Attachment';
import { FileAttachApi } from '../../app-data/state/FileAttachApi';
import { FancyFirebaseFileAttachApiBuilder } from '../../services/fancy-firebase-api-builder/fancy-firebase-file-attach-api-builder-service';
import { FeatureFlagManager } from '../../services/FeatureFlagManager';
import { convertFileAttachSuffixes } from '../../utils/file-utils';
import { TicketContext } from '../ticket-edit-panel/ticket-context';
import { AttachmentsViewState } from './attachments-view-state';
import { ProjectFileAttachApi } from '../../app-data/state/ProjectFileAttachApi';

/**
 * Service which maintains state for the <attached-images> component and its sub-components
 */
@Injectable({
	providedIn: 'root',
})
export class AttachedFilesService implements OnDestroy {
	public viewState$: BehaviorSubject<AttachmentsViewState>; // view state for entire file attach component
	public file$: BehaviorSubject<Attachment[]>;
	public projectFile$: BehaviorSubject<Attachment[]>;
	public numFileSelection$: BehaviorSubject<number>; // number file selected in multi select mode - displayed in toolbar
	public fileSelected$: BehaviorSubject<Attachment>; // single selected file
	public acceptedFileExtension$: Observable<string[]>; // That array output is geared towards file selection dialog configuration.
	public fileSizeLimit$: Observable<number>;
	private destroy$: Subject<void>;

	public api: FileAttachApi;
	public projectFileAttachApi: ProjectFileAttachApi;
	public ticketContext: TicketContext;
	public fileSelection: Set<Attachment>; // multi selected files

	public numberOfFiles = 0;
	public attachmentFiles$ = new BehaviorSubject([]);

	public editable$: BehaviorSubject<boolean>; // Version of "this.ticketContext.ticketIsEditable$" that yields on subscription
	public tokenRefreshTimer: ReturnType<typeof setTimeout> = null;

	private updateThumbnailFileUrlsSubscription: Subscription;
	private updatePreviewFileUrlsSubscription: Subscription;

	private updateProjectFileUrlsSubscription: Subscription;

	constructor(
		public fancyFirebaseFileAttachApiBuilder: FancyFirebaseFileAttachApiBuilder,
		private featureFlagManager: FeatureFlagManager,
		private angularFireDatabase: AngularFireDatabase
	) {
		this.destroy$ = new Subject();
		this.file$ = new BehaviorSubject<Attachment[]>([]);
		this.projectFile$ = new BehaviorSubject<Attachment[]>([]);
		this.viewState$ = new BehaviorSubject(AttachmentsViewState.SingleSelectMode);
		this.numFileSelection$ = new BehaviorSubject<number>(0);
		this.fileSelected$ = new BehaviorSubject<Attachment>(null);
		this.fileSelection = new Set<Attachment>();
		this.viewState$
			.pipe(
				distinctUntilChanged(),
				skip(1),
				filter((state: AttachmentsViewState) => state === AttachmentsViewState.MultiSelectMode),
				tap(() => this.fileSelection.clear()),
				mapTo(this.fileSelection.size),
				takeUntil(this.destroy$)
			)
			.subscribe(this.numFileSelection$);

		// if there's ever the case where there are no attachments in list,
		// navigate out of carousel view and back to singleSelect view
		this.file$
			.pipe(
				filter((val) => val.length === 0),
				mapTo(AttachmentsViewState.SingleSelectMode),
				takeUntil(this.destroy$)
			)
			.subscribe(this.viewState$);

		this.projectFile$
			.pipe(
				filter((val) => val.length === 0),
				mapTo(AttachmentsViewState.SingleSelectMode),
				takeUntil(this.destroy$)
			)
			.subscribe(this.viewState$);

		// feature flags
		this.acceptedFileExtension$ = this.featureFlagManager
			.getFlag$(DynamicFlagTypes.FILE_ATTACH_SUFFIXES)
			.pipe(map((spaceSeparatedString) => convertFileAttachSuffixes(spaceSeparatedString)));
		this.fileSizeLimit$ = this.featureFlagManager
			.getFlag$(DynamicFlagTypes.FILE_ATTACH_SIZE_LIMIT_IN_MB)
			.pipe(map((sizeLimit) => parseInt(String(sizeLimit), 10)));
	}

	public setTicketContext(context: TicketContext) {
		this.ticketContext = context;

		// Make sure the "editable$" BehaviorSubject yields the current value upon subscription
		this.editable$ = new BehaviorSubject<boolean>(true);
		context.ticketIsEditable$?.pipe(takeUntil(this.destroy$)).subscribe(this.editable$);
	}

	/**
	 * Sets up a subscription to set/update the "thumbnailFileUrl" and "thumbnailFetched" fields for
	 * all of the attachments.
	 */
	public updateThumbnailFileUrls(): void {
		if (!this.updateThumbnailFileUrlsSubscription) {
			this.updateThumbnailFileUrlsSubscription = this.file$.subscribe((data) => {
				this.api.updateThumbnailFileUrls(data);
			});
		}
	}

	/**
	 * Sets up a subscription to set/update the "previewFileUrl" and "previewFetched" fields for
	 * all of the attachments.
	 */
	public updatePreviewFileUrls(): void {
		if (!this.updatePreviewFileUrlsSubscription) {
			this.updatePreviewFileUrlsSubscription = this.file$.subscribe((data) => {
				this.api.updatePreviewFileUrls(data);
			});
		}
	}

	/**
	 * Sets up a subscription to set/update the "fileUrl" field for all of the attachments.
	 */
	public updateProjectFileUrls(): void {
		if (!this.updateProjectFileUrlsSubscription) {
			this.updateProjectFileUrlsSubscription = this.projectFile$.subscribe((data) => {
				this.projectFileAttachApi.updateFileUrls(data);
			});
		}
	}

	public ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
		if (this.updatePreviewFileUrlsSubscription) {
			this.updatePreviewFileUrlsSubscription.unsubscribe();
			this.updatePreviewFileUrlsSubscription = null;
		}
		if (this.updateThumbnailFileUrlsSubscription) {
			this.updateThumbnailFileUrlsSubscription.unsubscribe();
			this.updateThumbnailFileUrlsSubscription = null;
		}
		if (this.updateProjectFileUrlsSubscription) {
			this.updateProjectFileUrlsSubscription.unsubscribe();
			this.updateProjectFileUrlsSubscription = null;
		}
		this.editable$?.complete();
		this.file$.complete();
		this.projectFile$.complete();
		this.viewState$.complete();
		this.numFileSelection$.complete();
		this.fileSelected$.complete();
		this.api?.destroy();
		this.projectFileAttachApi?.destroy();
		clearTimeout(this.tokenRefreshTimer);
	}

	/**
	 * Create FileAttachApi instance for ticket attachments
	 * @param projectId project id
	 * @param userId user id
	 * @param ticketId ticket id
	 * @param planId plan id
	 */
	public createApi(projectId: string, userId: string, ticketId: string, planId: string): void {
		if (this.api) {
			this.api.destroy();
			this.api = null;
		}

		this.api = this.fancyFirebaseFileAttachApiBuilder.getFileAttachApi(projectId, planId, ticketId, userId);
		this.api.file$.subscribe((data) => this.file$.next(data));
		// reset some data
		this.viewState$.next(AttachmentsViewState.SingleSelectMode);
		this.fileSelected$.next(null);
	}

	/**
	 * Create ProjectFileAttachApi instance for project attachments
	 * @param projectId project id
	 * @param userId user id
	 */
	public createProjectFileAttachApi(projectId: string, userId: string): void {
		if (this.projectFileAttachApi) {
			this.projectFileAttachApi.destroy();
			this.projectFileAttachApi = null;
		}

		this.projectFileAttachApi = this.fancyFirebaseFileAttachApiBuilder.getProjectFileAttachApi(
			projectId,
			userId
		);
		this.projectFileAttachApi.file$.subscribe((data) => this.projectFile$.next(data));
	}

	public changeViewState(newState: AttachmentsViewState) {
		this.viewState$.next(newState);
	}

	// pass null to unselect
	public selectFile(file: Attachment): void {
		const previousSelectedFile = this.fileSelected$?.getValue();
		// change previous selected file to database view
		if (previousSelectedFile) {
			this.api.abortAttachmentView(previousSelectedFile);
		}

		this.fileSelected$.next(file);
		if (file) {
			// change new selected file to edit view
			this.api.changeAttachmentView(file, DefaultSplitDataRenderType.edit);
			this.scrollToSelectedFile(file);
		}
	}

	public extensionIsOK(file: File, acceptedFileExtensions: string[]): boolean {
		if (acceptedFileExtensions.includes('*')) {
			return true;
		}
		const dotIndex = file.name.lastIndexOf('.');
		let filesTrimmedLowerCaseSuffix = '';
		if (dotIndex !== -1) {
			// -1 means "no suffix", so '' works for that
			filesTrimmedLowerCaseSuffix = file.name.slice(dotIndex).trim().toLowerCase();
		}
		for (const validExtension of acceptedFileExtensions) {
			if (validExtension.toLowerCase() === filesTrimmedLowerCaseSuffix) {
				return true;
			}
		}
		return false;
	}

	public scrollToSelectedFile(file: Attachment): void {
		const itemToScrollTo = document.getElementById(file.$id);
		// null check to ensure that the element actually exists
		if (itemToScrollTo) {
			itemToScrollTo.scrollIntoView({ behavior: 'smooth', inline: 'center' });
		}
	}

	/**
	 * Add file to checked selections, for multi select mode
	 * @param file Attachment
	 */
	public addToFileSelection(file: Attachment): void {
		this.fileSelection.add(file);
		this.numFileSelection$.next(this.fileSelection.size);
	}

	/**
	 * Remove file from checked selections, for multi select mode
	 * @param file Attachment
	 */
	public removeFromFileSelection(file: Attachment): void {
		this.fileSelection.delete(file);
		this.numFileSelection$.next(this.fileSelection.size);
	}

	/**
	 * Clear all selected files
	 * @param file Attachment
	 */
	public clearFileSelection(): void {
		this.fileSelection.clear();
		this.numFileSelection$.next(this.fileSelection.size);
	}

	public launchFullViewClicked(): void {
		if (this.fileSelected$.getValue().view.fileUrl) {
			window.open(this.fileSelected$.getValue().view.fileUrl, '_blank');
		} else {
			this.api
				.getFileUrl$(this.fileSelected$.getValue())
				.pipe(take(1), takeUntil(this.destroy$))
				.subscribe((url: string) => window.open(url, '_blank'));
		}
	}

	// Convenience getters off of the "ticketContext":
	public get projectId() {
		return this.ticketContext.projectId;
	}

	public get planId() {
		return this.ticketContext.planId;
	}
}
